import React, { useCallback, useMemo, useState } from 'react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import SelectScheduleGroupIcon from '@mui/icons-material/EventNote';
import {
  Alert,
  AppBar,
  Box,
  CircularProgress,
  Dialog,
  Grid,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
  Toolbar,
  Typography,
} from '@mui/material';

import { Station } from '@paragon/contract-library';
import { closeCurrentPopup } from '@paragon/popup-state';

import { loadScheduleLineGroupData, loadScheduleLineGroupsForActiveStation } from '../../store/actions/loadActions';
import { markBundleFinishedAssembly } from '../../store/actions/production';
import { getActiveStation } from '../../store/selectors/getActiveStation';
import { getAssemblyMemberPartsForActiveScheduleLineGroup } from '../../store/selectors/getAssemblyMemberPartsForActiveScheduleLineGroup';
import { getAssemblyPlatePartsForActiveScheduleLineGroup } from '../../store/selectors/getAssemblyPlatePartsForActiveScheduleLineGroup';
import { getBundleAssemblyMemberPartEntriesByBundleGuid } from '../../store/selectors/getBundleAssemblyMemberPartEntriesByBundleGuid';
import { getBundleAssemblyPlatePartEntriesByBundleGuid } from '../../store/selectors/getBundleAssemblyPlatePartEntriesByBundleGuid';
import { getBundleIsFinishedAssemblyOrLater } from '../../store/selectors/getBundleIsFinishedAssemblyOrLater';
import {
  getActiveScheduleLineGroup,
  getCurrentProductionComponentInstance,
} from '../../store/selectors/scheduleStationSelectors';
import { setActiveScheduleLineGroupGuid } from '../../store/slices/productionModelsSlice';
import { useAppDispatch, useAppSelector } from '../hooks';
import { AssemblyMemberPartDiagrams } from '../interface/panels/assembly-parts/AssemblyMemberPartDiagrams';
import { AssemblyPlateParts } from '../interface/panels/assembly-parts/AssemblyPlateParts';
import { BundleAssemblyMemberPartDiagrams } from '../interface/panels/assembly-parts/BundleAssemblyMemberPartDiagrams';
import { BundleAssemblyPlateParts } from '../interface/panels/assembly-parts/BundleAssemblyPlateParts';
import { PartsFilterSelection } from '../interface/panels/assembly-parts/getMemberFilter';
import { getDefaultPartsFilterSelection, PartsFilter } from '../interface/panels/assembly-parts/PartsFilter';
import { BundleLayout } from './BundleLayout';

function getView(station: Station | null): 'parts' | 'bundle-layout' {
  if (station === null) return 'parts';
  switch (station.role) {
    case 'Bundler':
      return 'bundle-layout';
    default:
      return 'parts';
  }
}

const genericErrorLoadingDataMessage = 'Error loading data. Please try again or contact Paragon Customer Support.';

export function ScheduleStationPartsAndBundleViewDialog() {
  const dispatch = useAppDispatch();
  const activeScheduleLineGroup = useAppSelector(getActiveScheduleLineGroup);
  const orderedBundlesByScheduleLineGroupGuid = useAppSelector(
    (state) => state.productionModels.orderedBundlesByScheduleLineGroupGuid,
  );
  const bundleAssemblyMemberPartEntriesByBundleGuid = useAppSelector(getBundleAssemblyMemberPartEntriesByBundleGuid);
  const bundleAssemblyPlatePartEntriesByBundleGuid = useAppSelector(getBundleAssemblyPlatePartEntriesByBundleGuid);
  const scheduleLineGroupAssemblyPlateParts = useAppSelector(getAssemblyPlatePartsForActiveScheduleLineGroup);
  const scheduleLineGroupAssemblyMemberParts = useAppSelector(getAssemblyMemberPartsForActiveScheduleLineGroup);
  const bundleIsFinishedAssemblyOrLater = useAppSelector(getBundleIsFinishedAssemblyOrLater);
  const activeScheduleLineGroupGuid = useAppSelector((state) => state.productionModels.activeScheduleLineGroupGuid);
  const orderedScheduleLineGroups = useAppSelector((state) => state.productionModels.orderedScheduleLineGroups);
  const station = useAppSelector(getActiveStation);
  const currentProductionComponentInstance = useAppSelector(getCurrentProductionComponentInstance);

  const activeScheduleLineGroupBundles = useMemo(
    () =>
      activeScheduleLineGroup === null
        ? null
        : (orderedBundlesByScheduleLineGroupGuid[activeScheduleLineGroup.guid] ?? null),
    [activeScheduleLineGroup, orderedBundlesByScheduleLineGroupGuid],
  );
  const activeScheduleLineGroupIsFinishedOrLater = useMemo(
    () =>
      activeScheduleLineGroupBundles !== null &&
      activeScheduleLineGroupBundles.every((bundle) => bundleIsFinishedAssemblyOrLater[bundle.guid]),
    [activeScheduleLineGroupBundles, bundleIsFinishedAssemblyOrLater],
  );

  const partsOrBundleLayout = getView(station);
  const [activeBundleGuidOrAll, setActiveBundleGuidOrAll] = useState<string | null>(
    partsOrBundleLayout === 'parts'
      ? 'all'
      : ((station?.currentProductionComponentInstanceGuid && currentProductionComponentInstance?.bundleGuid) ??
          (activeScheduleLineGroupBundles === null ? null : activeScheduleLineGroupBundles[0].guid)),
  );
  const [partsFilterSelection, setPartsFilterSelection] = useState<PartsFilterSelection>(
    getDefaultPartsFilterSelection(station),
  );
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const [loadingScheduleLineGroups, setLoadingScheduleLineGroups] = useState<boolean>(false);
  const [loadingScheduleLineGroupData, setLoadingScheduleLineGroupData] = useState<boolean>(false);
  const [errorLoadingData, setErrorLoadingData] = useState<string | null>(null);
  const [errorMarkingFinished, setErrorMarkingFinished] = useState<boolean>(false);

  const advanceToNextBundle = useCallback(() => {
    if (activeScheduleLineGroupBundles === null) return;

    if (activeBundleGuidOrAll === 'all') setActiveBundleGuidOrAll(activeScheduleLineGroupBundles[0].guid);
    else {
      const currentBundleIndex = activeScheduleLineGroupBundles.findIndex(
        (bundle) => bundle.guid === activeBundleGuidOrAll,
      );
      if (currentBundleIndex === -1 || currentBundleIndex === activeScheduleLineGroupBundles.length - 1) return;
      const nextBundleGuid = activeScheduleLineGroupBundles[currentBundleIndex + 1].guid;
      setActiveBundleGuidOrAll(nextBundleGuid);
    }
  }, [activeScheduleLineGroupBundles, activeBundleGuidOrAll, setActiveBundleGuidOrAll]);

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      event.stopPropagation();
      event.preventDefault();
      if (event.key === ' ') {
        if (
          activeBundleGuidOrAll !== 'all' &&
          partsOrBundleLayout === 'bundle-layout' &&
          activeBundleGuidOrAll !== null &&
          !bundleIsFinishedAssemblyOrLater[activeBundleGuidOrAll] &&
          station &&
          station.role === 'Bundler'
        ) {
          dispatch(markBundleFinishedAssembly(activeBundleGuidOrAll)).then((success) => {
            setErrorMarkingFinished(!success);
          });
        }
        advanceToNextBundle();
      }
    },
    [
      activeBundleGuidOrAll,
      partsOrBundleLayout,
      bundleIsFinishedAssemblyOrLater,
      station,
      dispatch,
      advanceToNextBundle,
    ],
  );

  return (
    <Dialog open fullScreen onKeyDown={onKeyDown}>
      <AppBar sx={{ position: 'relative', mb: 2 }}>
        <Toolbar>
          <IconButton edge="start" onClick={() => void dispatch(closeCurrentPopup())}>
            <CloseIcon />
          </IconButton>

          <Typography sx={{ mx: 2 }} variant="h6">
            {`${partsOrBundleLayout === 'parts' ? 'Parts' : 'Bundle Layout'}${
              activeScheduleLineGroup === null ? '' : ` for ${activeScheduleLineGroup.name}`
            }`}
          </Typography>

          <IconButton
            edge="start"
            onClick={(event) => {
              setAnchorEl(event.currentTarget);
              setLoadingScheduleLineGroups(true);
              dispatch(loadScheduleLineGroupsForActiveStation(false)).then((success) => {
                setLoadingScheduleLineGroups(false);
                setErrorLoadingData(success ? null : genericErrorLoadingDataMessage);
              });
            }}
          >
            <SelectScheduleGroupIcon />
          </IconButton>
          <Popover
            open={!!anchorEl}
            anchorEl={anchorEl}
            onClose={() => void setAnchorEl(null)}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
          >
            {loadingScheduleLineGroups ? (
              <CircularProgress color="secondary" />
            ) : (
              <List>
                {orderedScheduleLineGroups.map((scheduleLineGroup) => (
                  <ListItemButton
                    key={scheduleLineGroup.guid}
                    selected={scheduleLineGroup.guid === activeScheduleLineGroupGuid}
                    onClick={async () => {
                      if (scheduleLineGroup.guid === activeScheduleLineGroupGuid) return;
                      setAnchorEl(null);
                      dispatch(setActiveScheduleLineGroupGuid(scheduleLineGroup.guid));
                      setLoadingScheduleLineGroupData(true);
                      const scheduleLineGroupData = await dispatch(loadScheduleLineGroupData(scheduleLineGroup, false));
                      setLoadingScheduleLineGroupData(false);
                      setErrorLoadingData(scheduleLineGroupData ? null : genericErrorLoadingDataMessage);
                      setActiveBundleGuidOrAll(
                        partsOrBundleLayout === 'parts'
                          ? 'all'
                          : (scheduleLineGroupData?.orderedBundles[0]?.guid ?? null),
                      );
                    }}
                  >
                    <ListItemText primary={scheduleLineGroup.name} />
                  </ListItemButton>
                ))}
              </List>
            )}
          </Popover>
          {errorLoadingData && <Alert severity="error">Error loading data</Alert>}
          {errorMarkingFinished && <Alert severity="error">Error marking finished</Alert>}
        </Toolbar>
        {loadingScheduleLineGroupData && <LinearProgress color="secondary" />}
      </AppBar>

      {!loadingScheduleLineGroupData && activeScheduleLineGroupBundles !== null ? (
        <Grid container spacing={1} sx={{ height: '100%', overflow: 'hidden' }}>
          <Grid size={2} sx={{ height: '100%', overflow: 'auto' }}>
            <List>
              {partsOrBundleLayout === 'parts' && (
                <ListItem
                  key="whole-schedule-line-group"
                  // We use custom styling here because we are not using a `ListItemButton`. We are not using a
                  // `ListItemButton` because there were issues with using the space bar to advance that.
                  sx={{
                    backgroundColor: activeBundleGuidOrAll === 'all' ? 'primary.dark' : undefined,
                    '&:hover': { cursor: 'pointer', backgroundColor: '#444' },
                  }}
                  onClick={() => {
                    setActiveBundleGuidOrAll('all');
                  }}
                >
                  {activeScheduleLineGroupIsFinishedOrLater && (
                    <ListItemIcon>
                      <CheckCircleIcon color="primary" />
                    </ListItemIcon>
                  )}
                  <ListItemText inset={!activeScheduleLineGroupIsFinishedOrLater} primary="All" />
                </ListItem>
              )}
              {activeScheduleLineGroupBundles.map((bundle) => (
                <ListItem
                  key={bundle.guid}
                  // We use custom styling here because we are not using a `ListItemButton`. We are not using a
                  // `ListItemButton` because there were issues with using the space bar to advance that.
                  sx={{
                    backgroundColor: activeBundleGuidOrAll === bundle.guid ? 'primary.dark' : undefined,
                    '&:hover': { cursor: 'pointer', backgroundColor: '#444' },
                  }}
                  onClick={(event) => {
                    event.stopPropagation();
                    event.preventDefault();
                    setActiveBundleGuidOrAll(bundle.guid);
                  }}
                >
                  {bundleIsFinishedAssemblyOrLater[bundle.guid] && (
                    <ListItemIcon>
                      <CheckCircleIcon color="primary" />
                    </ListItemIcon>
                  )}
                  <ListItemText inset={!bundleIsFinishedAssemblyOrLater[bundle.guid]} primary={`Bundle ${bundle.id}`} />
                </ListItem>
              ))}
            </List>
          </Grid>
          <Grid size={10} sx={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
            {partsOrBundleLayout === 'parts' && activeBundleGuidOrAll !== null && (
              <Box sx={{ height: '100%', overflow: 'hidden' }}>
                <Box sx={{ my: 1, ml: 1 }}>
                  <PartsFilter
                    partsFilterSelection={partsFilterSelection}
                    setPartsFilterSelection={setPartsFilterSelection}
                  />
                </Box>
                <Box sx={{ height: '80%', overflow: 'auto', mb: 8 }}>
                  {activeBundleGuidOrAll === 'all' ? (
                    partsFilterSelection === 'plates' ? (
                      <AssemblyPlateParts assemblyPlateParts={scheduleLineGroupAssemblyPlateParts} />
                    ) : (
                      <AssemblyMemberPartDiagrams
                        assemblyMemberParts={scheduleLineGroupAssemblyMemberParts}
                        partsFilterSelection={partsFilterSelection}
                      />
                    )
                  ) : partsFilterSelection === 'plates' ? (
                    <BundleAssemblyPlateParts
                      bundlePlatePartEntries={[bundleAssemblyPlatePartEntriesByBundleGuid[activeBundleGuidOrAll]]}
                      showBundleIdHeader={false}
                    />
                  ) : (
                    <BundleAssemblyMemberPartDiagrams
                      bundleMemberParts={[bundleAssemblyMemberPartEntriesByBundleGuid[activeBundleGuidOrAll]]}
                      partsFilterSelection={partsFilterSelection}
                      showBundleIdHeader={false}
                    />
                  )}
                </Box>
              </Box>
            )}
            {partsOrBundleLayout === 'bundle-layout' &&
              activeBundleGuidOrAll !== null &&
              activeBundleGuidOrAll !== 'all' && (
                <BundleLayout
                  bundleGuid={activeBundleGuidOrAll}
                  advanceToNextBundle={advanceToNextBundle}
                  setErrorMarkingFinished={setErrorMarkingFinished}
                />
              )}
          </Grid>
        </Grid>
      ) : (
        !loadingScheduleLineGroupData && (
          <Typography variant="h6" sx={{ ml: 2 }}>
            Use the calendar icon in the header to select a Schedule Line Group.
          </Typography>
        )
      )}
    </Dialog>
  );
}
